import { StockWithVariant } from '@/api/daily-stock-api';
import { useToast } from '@/hooks/use-toast';
import { VariantStockSearchItem } from '@/types/product-type';
import { useState } from 'react';

interface UseProductStockAdjustmentOptions {
  onSuccess?: () => void;
}

export function useProductStockAdjustment(options?: UseProductStockAdjustmentOptions) {
  const { toast } = useToast();
  const [adjustInDialogState, setAdjustInDialogState] = useState<{
    isOpen: boolean;
    selectedStock: StockWithVariant | null;
    dailyStockId: string | null;
  }>({
    isOpen: false,
    selectedStock: null,
    dailyStockId: null,
  });

  const [adjustOutDialogState, setAdjustOutDialogState] = useState<{
    isOpen: boolean;
    selectedStock: StockWithVariant | null;
    dailyStockId: string | null;
  }>({
    isOpen: false,
    selectedStock: null,
    dailyStockId: null,
  });

  // Convert VariantStockSearchItem to StockWithVariant using dailyStockId
  const convertVariantToStockWithVariant = (
    variant: VariantStockSearchItem,
  ): StockWithVariant | null => {
    if (!variant.dailyStockId) {
      return null;
    }

    // Create a minimal StockWithVariant object with dailyStockId as the id
    return {
      id: variant.dailyStockId?.toString() || "0",
      storeId: "0", // Not available in VariantStockSearchItem
      productId: variant.productId,
      variantId: variant.id.toString(),
      stockDate: '', // Not available in VariantStockSearchItem
      openQty: variant.openQty,
      stockInHand: variant.stockInHand,
      soldQty: variant.soldQty,
      receiveQty: variant.receiveQty,
      adjustInQty: variant.adjustInQty,
      adjustOutQty: variant.adjustOutQty,
      closingQty: variant.closingQty,
      locked: null,
      variantName: variant.variantName,
      sku: variant.sku,
      imageUrl: variant.imageUrl ?? null,
      price: variant.price ?? null,
      isActive: null, // Not available in VariantStockSearchItem
    };
  };

  // Handle adjust in
  const handleAdjustIn = async (variant: VariantStockSearchItem) => {
    if (!variant.dailyStockId) {
      toast({
        title: 'Error',
        description: 'Daily stock ID is missing. Please refresh the page.',
        variant: 'destructive',
      });
      return;
    }

    const stock = convertVariantToStockWithVariant(variant);
    if (stock) {
      const dailyStockIdString = typeof variant.dailyStockId === 'string'
        ? variant.dailyStockId
        : variant.dailyStockId.toString();
      setAdjustInDialogState({
        isOpen: true,
        selectedStock: stock,
        dailyStockId: dailyStockIdString,
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to load stock information.',
        variant: 'destructive',
      });
    }
  };

  // Handle adjust out
  const handleAdjustOut = async (variant: VariantStockSearchItem) => {
    if (!variant.dailyStockId) {
      toast({
        title: 'Error',
        description: 'Daily stock ID is missing. Please refresh the page.',
        variant: 'destructive',
      });
      return;
    }

    const stock = convertVariantToStockWithVariant(variant);
    if (stock) {
      const dailyStockIdString = typeof variant.dailyStockId === 'string'
        ? variant.dailyStockId
        : variant.dailyStockId.toString();
      setAdjustOutDialogState({
        isOpen: true,
        selectedStock: stock,
        dailyStockId: dailyStockIdString,
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to load stock information.',
        variant: 'destructive',
      });
    }
  };

  return {
    adjustInDialogState,
    adjustOutDialogState,
    handleAdjustIn,
    handleAdjustOut,
    setAdjustInDialogState,
    setAdjustOutDialogState,
  };
}
